import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionHeading from '@/components/feature/SectionHeading';
import { usePageMeta } from '@/hooks/usePageMeta';

const guides = [
  {
    icon: 'ri-search-2-line',
    title: '搜索影片',
    desc: '在顶部搜索框输入片名、主演或类型关键词，即可快速定位想看的电影。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '筛选与排序',
    desc: '在分类页与搜索页按类型、年代筛选，并按最新、最热或评分排序。',
  },
  {
    icon: 'ri-file-text-line',
    title: '查看剧情',
    desc: '点击任意海报进入详情页，阅读完整剧情简介、导演主演与上映信息。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '收藏影片',
    desc: '在详情页点击收藏，喜欢的电影会保存在你的本地浏览器，方便随时回顾。',
  },
];

const contactItems = [
  { icon: 'ri-mail-line', label: '反馈邮箱', value: 'hello@renjianzhongdu-demo.com' },
  { icon: 'ri-customer-service-2-line', label: '合作咨询', value: 'bd@renjianzhongdu-demo.com' },
  { icon: 'ri-map-pin-line', label: '办公地点', value: '中国大陆 · 线上运营' },
  { icon: 'ri-time-line', label: '响应时间', value: '工作日 10:00 - 18:00' },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 人间中毒在线',
    description:
      '了解人间中毒在线的网站定位、发展背景与联系方式。我们致力于为影迷提供清晰的电影剧情介绍与便捷的观影辅助体验。',
    keywords: '人间中毒在线,关于我们,影视资源,电影剧情介绍,在线观看',
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <div className="relative w-full overflow-hidden">
          <div className="relative h-[240px] w-full md:h-[300px]">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20movie%20theater%20interior%20with%20rows%20of%20empty%20seats%20and%20a%20glowing%20screen%2C%20deep%20navy%20blue%20atmosphere%20with%20warm%20amber%20and%20red%20accent%20lights%2C%20moody%20premium%20cinema%20aesthetic%2C%20cinematic%20grain%2C%20no%20text&width=1600&height=600&seq=rj-about-banner&orientation=landscape"
              alt="人间中毒在线 影院氛围背景"
              title="人间中毒在线 - 电影在线观看与剧情介绍"
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/40"></div>
          </div>

          <div className="mx-auto mt-6 w-full max-w-[1280px] px-4 md:px-8">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <h1 className="text-3xl font-extrabold text-foreground-950 md:text-4xl">关于人间中毒在线</h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              一个为影迷而生的影视内容平台，帮你在海量影片中快速找到想看的电影，读懂每一个值得被讲述的好故事。
            </p>
          </div>
        </div>

        <section className="mx-auto mt-12 w-full max-w-[1280px] px-4 md:mt-16 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <SectionHeading icon="ri-quill-pen-line" title="网站背景与定位" />
              <div className="space-y-4 text-sm leading-8 text-foreground-700 md:text-base">
                <p>
                  人间中毒在线诞生于一群资深影迷对「找片难、看简介更烦」的共鸣。我们希望把散落在各处的影片信息重新整理，
                  用清晰的海报网格、详尽的剧情介绍与多维筛选，让发现一部好电影变成一件轻松愉快的事。
                </p>
                <p>
                  目前网站已收录动作、剧情、爱情、科幻、喜剧、悬疑、犯罪、恐怖、动画、奇幻、历史等多种类型的影片，
                  并持续更新最新上映与热门推荐内容，帮助不同口味的观众都能找到属于自己的那一部。
                </p>
                <p>
                  需要说明的是，本网站当前展示的全部影片资料与数据均为<strong className="text-foreground-900">虚构演示内容</strong>，
                  视频播放区也处于前端演示阶段，尚未接入真实播放源。我们正持续完善产品体验，
                  未来将逐步提供更加完整的观影辅助功能。
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[
                  { value: '24+', label: '收录影片' },
                  { value: '12', label: '内容分类' },
                  { value: '20', label: '最新上线' },
                  { value: '100%', label: '虚构演示' },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-4 text-center">
                    <p className="text-2xl font-extrabold text-accent-500">{stat.value}</p>
                    <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <SectionHeading icon="ri-contacts-line" title="联系与反馈" />
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <ul className="space-y-4">
                  {contactItems.map((item) => (
                    <li key={item.label} className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100/25 text-secondary-900">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">{item.label}</p>
                        <p className="mt-0.5 text-sm text-foreground-900">{item.value}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="mt-5 rounded-md border border-background-200 bg-background-50 p-3 text-xs leading-relaxed text-foreground-500">
                  以上联系方式为演示用途的虚拟信息，如你有内容合作或使用建议，欢迎来信交流。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto mt-12 w-full max-w-[1280px] px-4 md:mt-16 md:px-8">
          <SectionHeading icon="ri-book-2-line" title="使用指南" subtitle="四步轻松上手，快速找到你想看的电影" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {guides.map((guide, i) => (
              <div
                key={guide.title}
                className={`rounded-lg border border-background-200 bg-background-100 p-5 transition hover:-translate-y-1 hover:border-accent-400/70 anim-fade-up delay-${(i % 5) + 1}`}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-500/15 text-accent-700">
                  <i className={`${guide.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">
                  <span className="mr-1 text-accent-500">{String(i + 1).padStart(2, '0')}</span>
                  {guide.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{guide.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-100 px-6 py-8 text-center sm:flex-row sm:text-left">
            <div>
              <h3 className="text-lg font-bold text-foreground-950">准备好开始你的观影发现之旅了吗？</h3>
              <p className="mt-1 text-sm text-foreground-600">从最新上映到热门推荐，精彩内容等你探索。</p>
            </div>
            <Link
              to="/category"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-accent-600 dark:text-foreground-950 cursor-pointer"
            >
              <i className="ri-compasses-2-line"></i>
              浏览全部影片
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}